import { Portrait } from './Portrait';

// Tone-keyed accent colours for the hero layout.
const ACCENTS = {
  gold: { pill: '#ffb300', pillText: '#0d0a07', border: 'rgba(255,220,160,0.25)', rule: '#ffb300', meta: '#f2c86b' },
  jade: { pill: '#5fc79a', pillText: '#0d1c16', border: 'rgba(180,240,215,0.22)', rule: '#5fc79a', meta: '#9fe3c4' },
  rose: { pill: '#e06a80', pillText: '#1a0608', border: 'rgba(255,200,210,0.22)', rule: '#e06a80', meta: '#f0a6b4' },
  ink:  { pill: '#6c8bc2', pillText: '#0a0f1a', border: 'rgba(200,220,245,0.22)', rule: '#6c8bc2', meta: '#b7cef0' },
};

/**
 * AudioAltar — the shared hero block for every audio detail page.
 *
 * Props
 *   eyebrow       string  e.g. "RECITATIONS FROM · QARI #25"
 *   title         string  primary name in Instrument-Serif italic
 *   subtitle      string  optional second title line
 *   arabicName    string  right-aligned Amiri text
 *   tone          'gold' | 'jade' | 'rose' | 'ink'
 *   facts         array of exactly 4 { label, value } objects
 *   bio           string  2–4 sentence bio paragraph
 *   quote         { text, attribution } pull-quote (optional)
 *   primaryCta    { label, onPlay }  yellow-pill play button
 *   portraitInitial  explicit initial override for Portrait
 *   portraitSize  number  defaults to 96
 */
export function AudioAltar({
  eyebrow,
  title,
  subtitle,
  arabicName,
  tone = 'gold',
  facts = [],
  bio,
  quote,
  primaryCta,
  portraitInitial,
  portraitSize = 96,
}) {
  const a = ACCENTS[tone] || ACCENTS.gold;

  return (
    <div style={{ padding: '16px 16px 0' }}>

      {/* ---- Hero block ---- */}
      <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', marginBottom: '16px' }}>
        <Portrait
          size={portraitSize}
          initial={portraitInitial}
          nameArabic={arabicName}
          tone={tone}
        />
        <div style={{ flex: 1, minWidth: 0, paddingTop: '4px' }}>
          {eyebrow && (
            <div style={{
              fontSize: '9px', letterSpacing: '0.12em', fontWeight: 700,
              color: a.meta, textTransform: 'uppercase', marginBottom: '4px',
              fontFamily: 'monospace',
            }}>
              {eyebrow}
            </div>
          )}
          <div style={{
            fontSize: '18px', fontWeight: 800, color: 'var(--text)',
            lineHeight: 1.2, fontStyle: 'italic',
            fontFamily: '"Georgia", "Times New Roman", serif',
          }}>
            {title}
          </div>
          {subtitle && (
            <div style={{
              fontSize: '15px', fontWeight: 700, color: 'var(--text)',
              lineHeight: 1.2, fontStyle: 'italic',
              fontFamily: '"Georgia", "Times New Roman", serif',
              marginTop: '2px',
            }}>
              {subtitle}
            </div>
          )}
          {arabicName && (
            <div style={{
              fontSize: '16px', color: a.meta,
              fontFamily: '"Amiri", "Scheherazade New", serif',
              direction: 'rtl', textAlign: 'right', marginTop: '6px',
            }}>
              {arabicName}
            </div>
          )}
        </div>
      </div>

      {/* ---- Facts strip (4 columns, thin dividers) ---- */}
      {facts.length === 4 && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
          borderTop: `1px solid ${a.border}`,
          borderBottom: `1px solid ${a.border}`,
          marginBottom: '14px',
        }}>
          {facts.map((f, i) => (
            <div key={i} style={{
              padding: '10px 0', textAlign: 'center',
              borderLeft: i > 0 ? `1px solid ${a.border}` : 'none',
            }}>
              <div style={{
                fontSize: '8px', letterSpacing: '0.1em', fontWeight: 700,
                color: 'var(--muted)', textTransform: 'uppercase',
                marginBottom: '3px', fontFamily: 'monospace',
              }}>
                {f.label}
              </div>
              <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text)' }}>
                {f.value}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ---- Bio + pull-quote ---- */}
      {(bio || quote) && (
        <div style={{ marginBottom: '14px' }}>
          {bio && (
            <div style={{
              fontSize: '12px', color: 'var(--muted)',
              lineHeight: 1.6, marginBottom: quote ? '10px' : 0,
            }}>
              {bio}
            </div>
          )}
          {quote && (
            <div style={{ borderLeft: `3px solid ${a.rule}`, paddingLeft: '12px' }}>
              <div style={{
                fontSize: '13px', fontStyle: 'italic', color: 'var(--text)',
                fontFamily: '"Georgia", serif', lineHeight: 1.5,
              }}>
                “{quote.text}”
              </div>
              {quote.attribution && (
                <div style={{
                  fontSize: '10px', color: a.meta,
                  fontWeight: 600, marginTop: '4px',
                }}>
                  — {quote.attribution}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ---- Primary CTA ---- */}
      {primaryCta && (
        <div style={{ marginBottom: '16px' }}>
          <button
            onClick={primaryCta.onPlay}
            style={{
              padding: '10px 20px',
              background: a.pill,
              border: 'none',
              borderRadius: '100px',
              color: a.pillText,
              fontSize: '13px',
              fontWeight: 800,
              cursor: 'pointer',
              letterSpacing: '0.02em',
            }}
          >
            {primaryCta.label}
          </button>
        </div>
      )}
    </div>
  );
}
